import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { Link } from "react-router-dom";

import {
  useOrgFacilitiesQuery,
  useSelectedFacility,
} from "@/app/FacilityContext";
import { useMyOrg } from "@/hooks/use-my-org";
import type { FacilityListItem } from "@/libs/facilityApi";

const UNLOCKED = new Set(["approved", "active"]);
const SEARCHABLE_FACILITY_COUNT = 3;

function typeLabel(type: string | null | undefined): string {
  switch (type) {
    case "coach_partner":
      return "Coach partner";
    case "merchant":
      return "Merchant";
    case "venue":
      return "Venue";
    default:
      return "Facility";
  }
}

function FacilityRow({
  facility,
  selected,
  onPick,
}: {
  facility: FacilityListItem;
  selected: boolean;
  onPick: () => void;
}) {
  const location = facility.location.trim() || "Location not set";
  const hasDistinctType =
    facility.facility_type === "coach_partner" ||
    facility.facility_type === "merchant";
  const accessibleName = hasDistinctType
    ? `${facility.name} ${location}. ${typeLabel(facility.facility_type)}`
    : `${facility.name} ${location}`;

  return (
    <MenuItem
      aria-label={accessibleName}
      aria-current={selected ? "true" : undefined}
      selected={selected}
      onClick={onPick}
      sx={{ gap: 1, alignItems: "center", whiteSpace: "normal" }}
    >
      <ListItemText
        primary={facility.name}
        secondary={location}
        slotProps={{
          primary: {
            variant: "body2",
            sx: {
              fontWeight: 700,
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              lineHeight: 1.35,
            },
          },
          secondary: {
            variant: "caption",
            sx: {
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              lineHeight: 1.35,
            },
          },
        }}
      />
      {hasDistinctType ? (
        <Chip
          size="small"
          variant="outlined"
          label={typeLabel(facility.facility_type)}
          sx={{ flexShrink: 0 }}
        />
      ) : null}
    </MenuItem>
  );
}

/** True when the facility selector renders (an unlocked org with facilities). */
export function useFacilitySelectorAvailable(): boolean {
  const orgView = useMyOrg();
  const locked = !UNLOCKED.has(orgView.org?.status ?? "");
  const { data: facilities } = useOrgFacilitiesQuery(
    locked ? undefined : orgView.org?.org_id,
  );
  return !locked && Boolean(facilities && facilities.length > 0);
}

export function FacilitySelector({
  variant = "field",
  triggerRef: externalTriggerRef,
}: {
  /** "plain": the phone top bar's borderless facility name. */
  variant?: "field" | "plain";
  /** Lets the phone mode switcher open this selector (Switch facility). */
  triggerRef?: { current: HTMLButtonElement | null };
} = {}) {
  const plain = variant === "plain";
  const { facilityId, setFacility } = useSelectedFacility();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchFocusedRef = useRef(false);
  const focusSearchOnOpenRef = useRef(false);
  const restoreTriggerFocusRef = useRef(false);

  const orgView = useMyOrg();
  const orgId = orgView.org?.org_id;
  const locked = !UNLOCKED.has(orgView.org?.status ?? "");
  const canCreateFacility = Boolean(orgView.org?.owner_user_id);
  const { data: facilities } = useOrgFacilitiesQuery(
    locked ? undefined : orgId,
  );
  const canSearchFacilities =
    (facilities?.length ?? 0) >= SEARCHABLE_FACILITY_COUNT;
  const selectorAvailable =
    !locked && Boolean(facilities && facilities.length > 0);
  const open = Boolean(anchorEl) && selectorAvailable;

  const closeSelector = (restoreTriggerFocus = false) => {
    focusSearchOnOpenRef.current = false;
    searchFocusedRef.current = false;
    restoreTriggerFocusRef.current = restoreTriggerFocus;
    setAnchorEl(null);
    setSearch("");
  };

  // Disclosure state belongs to the selected organization.
  useLayoutEffect(() => {
    focusSearchOnOpenRef.current = false;
    searchFocusedRef.current = false;
    setAnchorEl(null);
    setSearch("");
  }, [orgId]);

  // A refetch below the threshold removes and clears search without stealing
  // focus later if the field returns.
  useLayoutEffect(() => {
    if (canSearchFacilities) return;
    setSearch("");
    if (searchFocusedRef.current) {
      focusSearchOnOpenRef.current = false;
      searchFocusedRef.current = false;
      restoreTriggerFocusRef.current = true;
      setAnchorEl(null);
    }
  }, [canSearchFacilities]);

  // Lock/list changes fully reset a disclosure that is no longer available.
  useLayoutEffect(() => {
    if (selectorAvailable) return;
    focusSearchOnOpenRef.current = false;
    searchFocusedRef.current = false;
    setAnchorEl(null);
    setSearch("");
  }, [selectorAvailable]);

  if (locked || !facilities || facilities.length === 0) return null;

  const selected =
    facilities.find((facility) => facility.facility_id === facilityId) ?? null;
  const selectedName = selected?.name ?? null;
  const selectedLocation = selected?.location.trim() || null;
  const triggerLabel = selectedName
    ? `Facility selector. Current facility: ${selectedName}${selectedLocation ? ` ${selectedLocation}` : ""}`
    : "Facility selector";
  const normalizedSearch = canSearchFacilities
    ? search.trim().toLocaleLowerCase()
    : "";
  const filteredFacilities = normalizedSearch
    ? facilities.filter((facility) =>
        `${facility.name}\n${facility.location}`
          .toLocaleLowerCase()
          .includes(normalizedSearch),
      )
    : facilities;

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeSelector(true);
      return;
    }
    // Keep text-entry keys out of MenuList's native typeahead.
    event.stopPropagation();
  };

  return (
    <>
      <ButtonBase
        ref={(node: HTMLButtonElement | null) => {
          triggerRef.current = node;
          if (externalTriggerRef) externalTriggerRef.current = node;
        }}
        type="button"
        aria-label={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => {
          if (open) {
            closeSelector();
            return;
          }
          focusSearchOnOpenRef.current = canSearchFacilities;
          setAnchorEl(event.currentTarget);
        }}
        sx={{
          minWidth: 0,
          maxWidth: { xs: 150, md: 220 },
          borderRadius: "6px",
          textAlign: "left",
          "&:hover > span": { bgcolor: "action.hover" },
        }}
      >
        {/* Touch-target hit area around the compact top-bar field. */}
        <Box
          component="span"
          sx={{
            display: "flex",
            minWidth: 0,
            alignItems: "center",
            gap: 0.25,
            height: 28,
            border: plain ? 0 : 1,
            borderColor: "divider",
            borderRadius: "6px",
            pl: plain ? 0.5 : 0.875,
            pr: 0.5,
          }}
        >
          <Typography
            component="span"
            noWrap
            sx={{
              minWidth: 0,
              fontSize: plain ? 12.5 : 13,
              fontWeight: plain ? 500 : 600,
              lineHeight: 1.25,
              color: plain ? "text.secondary" : "text.primary",
            }}
          >
            {open ? "Facilities" : (selectedName ?? "Select facility")}
          </Typography>
          <ExpandMoreRoundedIcon
            aria-hidden="true"
            sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }}
          />
        </Box>
      </ButtonBase>

      <Menu
        anchorEl={anchorEl}
        open={open}
        autoFocus={!canSearchFacilities}
        onClose={(_event, reason) => closeSelector(reason === "escapeKeyDown")}
        slotProps={{
          paper: {
            sx: {
              width: "min(32rem, calc(100vw - 2rem))",
              maxHeight: "70vh",
            },
          },
          list: {
            "aria-label": "Facilities",
            autoFocusItem: !canSearchFacilities,
          },
          transition: {
            onEntered: () => {
              if (!focusSearchOnOpenRef.current) return;
              focusSearchOnOpenRef.current = false;
              searchInputRef.current?.focus();
            },
            onExited: () => {
              if (!restoreTriggerFocusRef.current) return;
              restoreTriggerFocusRef.current = false;
              triggerRef.current?.focus();
            },
          },
        }}
      >
        {canSearchFacilities ? (
          <ListSubheader disableSticky sx={{ px: 1, py: 0.75 }}>
            <TextField
              inputRef={searchInputRef}
              type="search"
              size="small"
              fullWidth
              label="Search facilities"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onFocus={() => {
                searchFocusedRef.current = true;
              }}
              onBlur={() => {
                searchFocusedRef.current = false;
              }}
              onKeyDown={handleSearchKeyDown}
            />
          </ListSubheader>
        ) : null}

        {filteredFacilities.length > 0 ? (
          filteredFacilities.map((facility) => (
            <FacilityRow
              key={facility.facility_id}
              facility={facility}
              selected={facility.facility_id === facilityId}
              onPick={() => {
                setFacility(
                  facility.facility_id,
                  facility.name,
                  facility.facility_type,
                );
                closeSelector(true);
              }}
            />
          ))
        ) : (
          <MenuItem disabled>
            <Typography
              role="status"
              variant="body2"
              color="text.secondary"
              sx={{ py: 0.5 }}
            >
              No facilities match your search.
            </Typography>
          </MenuItem>
        )}

        {canCreateFacility
          ? [
              <Divider key="new-facility-divider" component="li" />,
              <MenuItem
                key="new-facility"
                component={Link}
                to="/facilities/new"
                onClick={() => closeSelector()}
              >
                <AddRoundedIcon
                  fontSize="small"
                  sx={{ mr: 1.5 }}
                  aria-hidden="true"
                />
                New facility
              </MenuItem>,
            ]
          : null}
      </Menu>
    </>
  );
}
